import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, Divider, Paper, Stack, Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

/**
 * AccessDeniedPanel — what a refused page looks like (RBAC Phase 3).
 *
 * Rendered in place of the page when the navigation registry denies the route,
 * inside the normal layout so the user keeps their sidebar and can go
 * somewhere they ARE allowed.
 *
 * **The codename is the point.** "You do not have access to this page" sends
 * the user to raise a ticket that a developer has to translate; naming
 * `srm.view_purchase_order` lets their administrator act on it in
 * /admin/rbac without anyone reading Python. Same principle as
 * `ReferenceDataBanner` and `HasModuleAccess._deny`.
 */
// Defaults are declared as parameters, not `defaultProps` — React 18.3 warns
// on the latter for function components.
export default function AccessDeniedPanel({ gate = null, path = '', reason = 'denied' }) {
  const navigate = useNavigate();
  const required = (gate && gate.required) || [];
  const hidden = gate && gate.visibility === 'HIDDEN';
  const unknown = reason === 'unknown_route';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, md: 6 } }}>
      <Paper elevation={0} sx={{ maxWidth: 640, width: '100%', p: 4, border: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <LockOutlinedIcon color="warning" />
          <Typography variant="h6">You do not have access to this page</Typography>
        </Stack>

        {gate && gate.label && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>{gate.label}</strong>
            {path ? ` — ${path}` : ''}
          </Typography>
        )}

        {hidden && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            This page has been hidden for everyone by your administrator.
          </Typography>
        )}

        {!hidden && unknown && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            This page is not in your organisation&apos;s page registry, and access
            to unregistered pages is switched off. Ask your administrator to
            register it under Admin → RBAC → Pages.
          </Typography>
        )}

        {!hidden && !unknown && (
          <>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Ask your administrator to grant your role
              {required.length > 1 ? ' one of' : ''}:
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {required.length ? (
                required.map((codename) => (
                  <Chip key={codename} size="small" label={codename}
                        sx={{ fontFamily: 'monospace' }} />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  access to this page (Admin → RBAC → Pages)
                </Typography>
              )}
            </Stack>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={1}>
          <Button variant="contained" size="small" onClick={() => navigate('/')}>
            Go to dashboard
          </Button>
          <Button size="small" onClick={() => navigate(-1)}>Back</Button>
        </Stack>
      </Paper>
    </Box>
  );
}

AccessDeniedPanel.propTypes = {
  /** `route_gates[route]` from the navigation payload — label, visibility and
   *  the codenames that would grant the page. */
  gate: PropTypes.shape({
    label: PropTypes.string,
    key: PropTypes.string,
    reason: PropTypes.string,
    visibility: PropTypes.string,
    required: PropTypes.arrayOf(PropTypes.string),
  }),
  path: PropTypes.string,
  reason: PropTypes.string,
};
